import React, { useCallback } from 'react';

import moment from 'moment';

import { Button, Emphasis, Theme } from '@lumapps/lumx/react';
import { useSelector } from '@lumapps/redux/react';
import { useTranslate } from '@lumapps/translations';
import { isConnected } from '@lumapps/user/ducks/selectors';
import { getCookie, setCookie } from '@lumapps/utils/cookies/cookie_utils';

import { HAS_ACCEPTED_COOKIES, CONSENT_DURATION } from '../../constants';
import { isGDPRBannerEnabled } from '../../ducks/selectors';
import { GDPR_BANNER } from '../../keys';
import { disableAnalyticsTags, removeUnsafeCookies } from '../../utils';

import './index.scss';

export interface GdprBannerInterface {
    saveUserCookiesPreferences(choice: boolean): void;
    hasAcceptedCookies: boolean;
    userId: string | undefined;
}

const CLASSNAME = 'old-gdpr-banner';
const TITLE_ID = `${CLASSNAME}-title`;
/**
 * Displays the GDPR banner.
 *
 * @return {JSX.Element | null} Banner.
 */
export const GdprBanner = ({ saveUserCookiesPreferences, hasAcceptedCookies, userId }: GdprBannerInterface) => {
    const [isHidden, setIsHidden] = React.useState(true);
    const { translateKey } = useTranslate();
    const isUserConnected = useSelector(isConnected);
    const isFeatureEnabled = useSelector(isGDPRBannerEnabled);

    /**
     * Check if user has already closed the banner once (no matter if they accepted or refused).
     * We check on page loading and when the user has clicked on the banner.
     *
     * @return {boolean} Whether the user already made a choice.
     */
    const hasUserClosedBanner = React.useMemo(
        () => isHidden && getCookie(`${HAS_ACCEPTED_COOKIES}.${userId}`) !== undefined,
        [userId, isHidden],
    );

    /**
     * Check if user has accepted cookies or not.
     *
     * @return {boolean} Whether the user has accepted cookies or not.
     */
    const hasAcceptedBanner = React.useMemo(
        () =>
            hasUserClosedBanner &&
            (getCookie(`${HAS_ACCEPTED_COOKIES}.${userId}`) === 'true' || (isUserConnected && hasAcceptedCookies)),
        [hasAcceptedCookies, hasUserClosedBanner, isUserConnected, userId],
    );

    /**
     * Save that the user closed the GDPR banner in a cookie and if user is connected, save his choice.
     */
    const rememberUserChoice = useCallback(
        (choice: boolean) => {
            setCookie(
                `${HAS_ACCEPTED_COOKIES}.${userId}`,
                choice.toString(),
                moment().add(CONSENT_DURATION, 'months').toDate(),
            );

            // If user is connected, save his choice.
            if (isUserConnected) {
                saveUserCookiesPreferences(choice);
            } else if (!choice) {
                window.location.reload();
            }
        },
        [isUserConnected, saveUserCookiesPreferences, userId],
    );

    /**
     * On click event, save the user has accepted the use of cookies.
     */
    const onAccept = useCallback(() => {
        rememberUserChoice(true);
        setIsHidden(true);
    }, [rememberUserChoice]);

    /**
     * On click event, save the user has declined the use of cookies.
     */
    const onDecline = useCallback(() => {
        setIsHidden(true);
        removeUnsafeCookies();
        rememberUserChoice(false);
    }, [rememberUserChoice]);

    /**
     * Whether we show or not the banner depending on all the settings before
     */
    React.useEffect(() => {
        if (hasUserClosedBanner) {
            if (!hasAcceptedBanner) {
                disableAnalyticsTags();
                removeUnsafeCookies();
            }
        } else {
            setIsHidden(false);
        }
    }, [hasUserClosedBanner, hasAcceptedBanner]);

    return !isHidden && isFeatureEnabled ? (
        <section className={CLASSNAME} aria-labelledby={TITLE_ID}>
            <div className={`${CLASSNAME}__wrapper`}>
                <div className={`${CLASSNAME}__content`}>
                    <h2 id={TITLE_ID} className={`${CLASSNAME}__title`}>
                        {translateKey(GDPR_BANNER.TITLE_BANNER)}
                    </h2>
                    <p className={`${CLASSNAME}__text`}>{translateKey(GDPR_BANNER.TEXT_BANNER)}</p>
                </div>
                <div className={`${CLASSNAME}__buttons`}>
                    <Button
                        className={`${CLASSNAME}__button lumx-spacing-margin-right-regular`}
                        emphasis={Emphasis.medium}
                        theme={Theme.dark}
                        onClick={onDecline}
                    >
                        {translateKey(GDPR_BANNER.DECLINE)}
                    </Button>
                    <Button className={`${CLASSNAME}__button`} theme={Theme.dark} onClick={onAccept}>
                        {translateKey(GDPR_BANNER.ACCEPT)}
                    </Button>
                </div>
            </div>
        </section>
    ) : null;
};

GdprBanner.displayName = 'GdprBanner';
